// RoleSwitch.tsx — Atrium top-bar role indicator (Sprint 82, ADR-082).
// ----------------------------------------------------------------------------
// SECURITY NOTE — this is intentionally NOT the prototype's free role toggle.
//
// In the prototype (nav2.jsx RoleSwitch) the chip switches `role` freely, which
// would be a client-side privilege escalation. Here the component reflects the
// user's ACTUAL authenticated role from authStore. It never mutates the role
// and never grants access the server wouldn't.
//
//   - Single-role user  → static, non-interactive chip showing their role.
//   - super-admin        → MAY switch the on-screen *view* (which role's nav
//     layout to preview). This is presentation only; real authorization is
//     always enforced server-side per request (TenancyGuard / @PreAuthorize),
//     and the menu items themselves stay gated by passesRoleGate. Switching the
//     view never elevates what the API will return.
//
// For Sprint 82 we keep it minimal: render the role as a static chip for every
// role. A super-admin view-switcher can be layered on later without changing
// this contract. No `setRole` is exposed to callers.
import React from 'react';

export interface RoleSwitchProps {
  /** Authenticated role (wide string from UnifiedAuthContext / authStore). */
  role: string | undefined;
}

const ROLE_LABEL: Record<string, string> = {
  owner:             'Owner',
  admin:             'Manager',
  'board-member':    'Board',
  super_admin_condo: 'Super Admin',
  superadmin:        'Super Admin',
};

function initials(label: string): string {
  const parts = label.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const chipStyle: React.CSSProperties = {
  height: 38, display: 'inline-flex', alignItems: 'center', gap: 9,
  padding: '0 13px', borderRadius: 10, background: 'var(--card)',
  border: '1px solid var(--line2)', color: 'var(--ink)',
  fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
};

const avtStyle: React.CSSProperties = {
  width: 24, height: 24, borderRadius: '50%', background: 'var(--blue-s)',
  color: 'var(--blue)', display: 'flex', alignItems: 'center',
  justifyContent: 'center', fontWeight: 800, fontSize: 10,
};

export const RoleSwitch: React.FC<RoleSwitchProps> = ({ role }) => {
  const label = (role && ROLE_LABEL[role]) || 'Member';
  return (
    <div
      style={chipStyle}
      title={`Signed in as ${label}`}
      aria-label={`Role: ${label}`}
    >
      <span style={avtStyle}>{initials(label)}</span>
      <span>{label}</span>
    </div>
  );
};
